import { ComponentChildren, Fragment, h } from "preact";
import { Radio } from "./radio";
import { PasswordField } from "./passwordfield";
import "./formfield.scss";
import { computePasswordStrength, PasswordStrength } from "./passwordstrength";
import { useCallback, useMemo, useState } from "preact/hooks";

export type FieldChangedCallback<T> = (value: T, id: string) => void;

type BaseProps = {
    id: string;
    name?: string;
    label: string;
    placeholder?: string;
    validationError?: string;
    className?: string;
    size?: "default" | "small" | "medium" | "large";
    state?: "valid" | "invalid";
    readonly?: boolean;
    disabled?: boolean;
    autoComplete?: string;
    onChange?: h.JSX.GenericEventHandler<HTMLInputElement>
}

type TextProps = {
    value?: string;
    type?: "text" | "email" | "tel" | "textarea";
    // todo: autocomplete, maxlength
    // todo: optional icon to place at the end
    // todo: optional prefix shown to the left (such as "https://")
};


type TextareaProps = {
    value?: string;
    type?: "textarea";
    // todo: maxlength
};

type PasswordProps = {
    value?: string;
    showStrength?: boolean;
    type: "password";
    // todo: autocomplete, maxlength
};

type NumberProps = {
    value?: number;
    type: "number" | "float" | "range";
    // todo: min, max
}

type ListProps = {
    value?: string;
    type: "select" | "radios";
    // todo: unselected placeholder text? "Select ..."
} & ({
    permittedValuesLookup: { [id: string]: string } | null;
} | {
    permittedValuesList: { id: string, name: string }[] | null;
});

type TemporalProps = {
    value?: any;
    type?: "date" | "datetime-local" | "week" | "month" | "time";
}

type Props = (BaseProps & TextProps) | (BaseProps & TextareaProps) | (BaseProps & PasswordProps) | (BaseProps & NumberProps) | (BaseProps & ListProps) | (BaseProps & TemporalProps);

export function FormField({ id, name, className, value, label, placeholder, validationError, size, state, type, disabled, autoComplete, readonly, onChange, ...props }: Props) {

    const css_size = (!size || size === "default") ? " medium" : (" " + size);

    const [passwordStrength, setPasswordStrenght] = useState(computePasswordStrength(value));
    const passwordCallback = useCallback((e: h.JSX.TargetedEvent<HTMLInputElement, Event>) => {
        const password = e.currentTarget.value;
        const strength = computePasswordStrength(password);
        setPasswordStrenght(strength);
        onChange?.(e);
    }, [setPasswordStrenght, onChange]);
    const selectCallback = useCallback((e: h.JSX.TargetedEvent<HTMLSelectElement, Event>) => {
        // const password = e.currentTarget.value;
        // const strength = computePasswordStrength(password);
        // setPasswordStrenght(strength);
        onChange?.(e as any);
    }, [onChange]);

    return (
        <div class={"formfield" + (!className ? "" : (" " + className))}>
            {
                (type == undefined || type == "text" || type == "tel" || type == "email" || type == "date" || type == "datetime-local" || type == "week" || type == "month" || type == "time") &&
                <input
                    id={id}
                    name={name}
                    type={type ?? "text"}
                    value={value}
                    placeholder={placeholder}
                    class={(state ?? "") + css_size}
                    readOnly={readonly}
                    autoComplete={autoComplete}
                    disabled={disabled}
                    onChange={onChange}
                />
            }
            {
                type == "password" &&
                <Fragment>
                    <PasswordField
                        id={id}
                        name={name}
                        value={value}
                        placeholder={placeholder}
                        state={state}
                        size={size}
                        disabled={disabled}
                        autoComplete={autoComplete}
                        callback={passwordCallback}
                    />
                    {
                        "showStrength" in props && props.showStrength &&
                        <PasswordStrength strength={passwordStrength} valid={passwordStrength >= 100} />
                    }
                </Fragment>
            }
            {
                (type == "number" || type == "float" || type == "range") &&
                <input
                    id={id}
                    name={name}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    class={(state ?? "") + css_size}
                    disabled={disabled}
                    onChange={onChange}
                />
            }
            {
                type == "textarea" &&
                <textarea
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    class={(state ?? "") + css_size}
                    disabled={disabled}
                    onChange={onChange as any}
                >{value}</textarea>
            }
            {
                type == "select" &&
                <select id={id} name={name} disabled={disabled} onChange={selectCallback}>
                    {
                        "permittedValuesLookup" in props && props.permittedValuesLookup &&
                        Object.keys(props.permittedValuesLookup).map(key => <option value={key} selected={key == value} data-selected={key == value}>{props.permittedValuesLookup![key]}</option>)
                    }
                    {
                        "permittedValuesList" in props && props.permittedValuesList &&
                        props.permittedValuesList.map(item => <option value={item.id} selected={item.id == value} data-selected={item.id == value}>{item.name}</option>)
                    }
                </select>
            }
            {
                type == "radios" && <Fragment>
                    {
                        "permittedValuesLookup" in props && props.permittedValuesLookup &&
                        Object.keys(props.permittedValuesLookup).map(key => <div>
                            <Radio id={id + "_" + key} name={id} value={key} checked={key == value} label={props.permittedValuesLookup![key]} disabled={disabled} />
                        </div>)
                    }
                    {
                        "permittedValuesList" in props && props.permittedValuesList &&
                        props.permittedValuesList.map(item => <div>
                            <Radio id={id + "_" + item.id} name={id} value={item.id} checked={item.id == value} label={item.name} disabled={disabled} />
                        </div>)
                    }
                </Fragment>
            }
            {
                validationError &&
                <span>{validationError}</span>
            }
            <label for={id} class={state}>{label}</label>
        </div>
    );
}
